import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Share2, Users, Send, Copy, Loader2 } from "lucide-react";

/**
 * Reusable share sheet. Lets a user post an achievement to the in-app
 * community feed and/or share it externally (Web Share API w/ clipboard fallback).
 */
export const ShareDialog = ({ open, onOpenChange, defaultText = "", title = "Share your achievement" }) => {
  const [text, setText] = useState(defaultText);
  const [posting, setPosting] = useState(false);

  // keep local text in sync when a new defaultText arrives
  React.useEffect(() => { setText(defaultText); }, [defaultText]);

  const postToCommunity = async () => {
    const body = (text || "").trim();
    if (!body) return;
    setPosting(true);
    try {
      await api.post("/community/messages", { channel: "general", text: body });
      toast.success("Shared to the community feed");
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not post to community");
    } finally {
      setPosting(false);
    }
  };

  const shareExternal = async () => {
    const body = (text || "").trim();
    if (!body) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Real Deal Fitness", text: body });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user cancelled
      }
    }
    try {
      await navigator.clipboard.writeText(body);
      toast.success("Copied to clipboard — paste anywhere to share");
    } catch (e) {
      toast.error("Sharing not supported on this device");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover border-border max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Share2 className="h-5 w-5 text-[#ccff0c]" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            data-testid="share-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            className="bg-card border-border text-sm"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid="share-to-community"
              onClick={postToCommunity}
              disabled={posting || !text.trim()}
              className="bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold"
            >
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Users className="h-4 w-4 mr-1.5" /> Community</>}
            </Button>
            <Button
              data-testid="share-external"
              onClick={shareExternal}
              disabled={!text.trim()}
              className="bg-secondary border border-border text-foreground hover:border-[#ccff0c]/40"
            >
              {navigator.share ? <><Send className="h-4 w-4 mr-1.5" /> Share</> : <><Copy className="h-4 w-4 mr-1.5" /> Copy</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
